import {OutgoingInvoiceId} from "../../models/accounting/OutgoingInvoice";
import {ErrorAction, ReduxAction} from "../../models/common/ActionTypes";
import {OutgoingInvoicePayment, OutgoingInvoicePaymentId} from "../../models/accounting/Payable";
import {Identifier} from "../../models/common/Identifier";
import {Observable, of} from "rxjs";
import {ofType, StateObservable} from "redux-observable";
import {AppState} from "../../reducers/RootReducer";
import {catchError, mergeMap, switchMap} from "rxjs/operators";
import {ajax} from "rxjs/ajax";
import {APP_CONFIG} from "../../config/GeneralConfig";
import {RouteMethods} from "../../config/RouteConfig";
import {eventNotificationError} from "../common/EventNotificationActions";
import {OutgoingInvoicePaymentModule} from "../../models/accounting/OutgoingInvoicePaymentModule";
import {fetchOutgoingInvoiceById, fetchOutgoingInvoices} from "./AccountBookActions";
import {Nullable} from "../../models/common/Nullable";
import {getInitialViewSettings} from "../../models/common/ViewSettings";
import {OutgoingInvoiceModule} from "../../models/accounting/AccountBook";


/** backend api path */
function apiPath( id: Nullable< number > = null ): string {
    const uri = 'invoices/outgoing/'
    if( id === null ) {
        return APP_CONFIG.baseWeb + uri;
    } else {
        return APP_CONFIG.baseWeb + uri  + id;
    }
}

/**
 *  insert invoice payment
 */
export interface InsertOutgoingInvoicePaymentAction extends OutgoingInvoiceId, ReduxAction< typeof INSERT_OUTGOING_INVOICE_PAYMENT > {
    outgoingInvoicePayment: OutgoingInvoicePayment
}
export interface InsertOutgoingInvoicePaymentSuccessAction extends ReduxAction<typeof INSERT_OUTGOING_INVOICE_PAYMENT_SUCCESS > {
}
export interface InsertOutgoingInvoicePaymentErrorAction extends ErrorAction< typeof INSERT_OUTGOING_INVOICE_PAYMENT_ERROR >{}

export type InsertOutgoingInvoicePaymentActionTypes = InsertOutgoingInvoicePaymentAction
    | InsertOutgoingInvoicePaymentErrorAction
    | InsertOutgoingInvoicePaymentSuccessAction

/**
 * redux action constants
 */
export const INSERT_OUTGOING_INVOICE_PAYMENT = "INSERT_OUTGOING_INVOICE_PAYMENTS";
export const INSERT_OUTGOING_INVOICE_PAYMENT_SUCCESS = "INSERT_OUTGOING_INVOICE_PAYMENTS_SUCCESS";
export const INSERT_OUTGOING_INVOICE_PAYMENT_ERROR = "INSERT_OUTGOING_INVOICE_PAYMENTS_ERROR";

/**
 * redux action functions
 */
export function insertOutgoingInvoicePayment(
    outgoingInvoiceId: Identifier< number >,
    outgoingInvoicePayment: OutgoingInvoicePayment
) : InsertOutgoingInvoicePaymentAction {
    return { type: INSERT_OUTGOING_INVOICE_PAYMENT, outgoingInvoicePayment, outgoingInvoiceId };
}
export function insertOutgoingInvoicePaymentSuccess() : InsertOutgoingInvoicePaymentSuccessAction {
    return { type: INSERT_OUTGOING_INVOICE_PAYMENT_SUCCESS };
}
export function insertOutgoingInvoicePaymentError( error: Error ): InsertOutgoingInvoicePaymentErrorAction {
    return { type: INSERT_OUTGOING_INVOICE_PAYMENT_ERROR, error };
}

/**
 * redux observable
 */
export function insertOutgoingInvoicePaymentEpic(
    action$: Observable< InsertOutgoingInvoicePaymentAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( INSERT_OUTGOING_INVOICE_PAYMENT ),
        switchMap(action => {
            const path = `${ apiPath( action.outgoingInvoiceId.rawValue ) }/payments`;

            return ajax(
                APP_CONFIG.getRequestObject(
                    path,
                    RouteMethods.post,
                    action.outgoingInvoicePayment
                )
            ).pipe(
                mergeMap(_ => of(
                    insertOutgoingInvoicePaymentSuccess(),
                    fetchOutgoingInvoiceById( action.outgoingInvoiceId ),
                    fetchOutgoingInvoices( getInitialViewSettings( OutgoingInvoiceModule.identifier ) )
                )),
                catchError(error => of(
                    insertOutgoingInvoicePaymentError( error),
                    eventNotificationError(error, store$, OutgoingInvoicePaymentModule.identifier, "insertOutgoingInvoicePayment")
                ))
            );

        })
    );
}

/**
 * delete invoice payment
 */
export interface DeleteOutgoingInvoicePaymentAction extends OutgoingInvoiceId, OutgoingInvoicePaymentId , ReduxAction< typeof DELETE_OUTGOING_INVOICE_PAYMENT > {

}
export interface DeleteOutgoingInvoicePaymentSuccessAction extends ReduxAction<typeof DELETE_OUTGOING_INVOICE_PAYMENT_SUCCESS > {
}
export interface DeleteOutgoingInvoicePaymentErrorAction extends ErrorAction< typeof DELETE_OUTGOING_INVOICE_PAYMENT_ERROR >{}

export type DeleteOutgoingInvoicePaymentActionTypes = DeleteOutgoingInvoicePaymentAction
    | DeleteOutgoingInvoicePaymentErrorAction
    | DeleteOutgoingInvoicePaymentSuccessAction

/**
 * redux action constants
 */
export const DELETE_OUTGOING_INVOICE_PAYMENT = "DELETE_OUTGOING_INVOICE_PAYMENTS";
export const DELETE_OUTGOING_INVOICE_PAYMENT_SUCCESS = "DELETE_OUTGOING_INVOICE_PAYMENTS_SUCCESS";
export const DELETE_OUTGOING_INVOICE_PAYMENT_ERROR = "DELETE_OUTGOING_INVOICE_PAYMENTS_ERROR";

/**
 * redux action functions
 */
export function deleteOutgoingInvoicePayment(
    outgoingInvoiceId: Identifier< number >,
    outgoingInvoicePaymentId: Identifier<number>
) : DeleteOutgoingInvoicePaymentAction {
    return { type: DELETE_OUTGOING_INVOICE_PAYMENT, id: outgoingInvoicePaymentId, outgoingInvoiceId };
}
export function deleteOutgoingInvoicePaymentSuccess() : DeleteOutgoingInvoicePaymentSuccessAction {
    return { type: DELETE_OUTGOING_INVOICE_PAYMENT_SUCCESS };
}
export function deleteOutgoingInvoicePaymentError( error: Error ): DeleteOutgoingInvoicePaymentErrorAction {
    return { type: DELETE_OUTGOING_INVOICE_PAYMENT_ERROR, error };
}

/**
 * redux observable
 */
export function deleteOutgoingInvoicePaymentEpic(
    action$: Observable< DeleteOutgoingInvoicePaymentAction >,
    store$: StateObservable< AppState >
) {
    return action$.pipe(
        ofType( DELETE_OUTGOING_INVOICE_PAYMENT ),
        switchMap(action => {
            const path = `${ apiPath( action.outgoingInvoiceId.rawValue ) }/payments/`+ action.id.rawValue;

            return ajax(
                APP_CONFIG.getRequestObject( path, RouteMethods.delete )
            ).pipe(
                mergeMap(_ => of(
                    deleteOutgoingInvoicePaymentSuccess(),
                    fetchOutgoingInvoiceById( action.outgoingInvoiceId )
                )),
                catchError(error => of(
                    deleteOutgoingInvoicePaymentError( error),
                    eventNotificationError(error, store$, OutgoingInvoicePaymentModule.identifier, "deleteOutgoingInvoicePayment")
                ))
            );

        })
    );
}